import { RequirementsExtractionResponse, FacilitatorGuidance } from './workshop';
import { RESPONSE_MODES, ChatMessage } from '../types';
import { api } from './api';

function buildResponseModeString(responseModeKey: string): string {
  const mode = RESPONSE_MODES[responseModeKey];
  if (!mode) return `Response Mode: ${responseModeKey}`;
  return `Response Mode: ${mode.name} - ${mode.description}`;
}

/**
 * Fire-and-forget: sends a request to n8n for avatar contributions.
 * Returns immediately with a requestId. Contributions arrive asynchronously
 * via the n8n callback → backend → health check polling flow.
 *
 * @param targetAvatarRoles - omit for all avatars (governor decides), provide specific roles for targeted response
 */
export async function callN8nWorkflow(
  sessionId: string,
  phase: string,
  phaseGuideline: string,
  responseModeKey: string,
  userInput?: string,
  projectId?: string,
  targetAvatarRoles?: string[],
  maxWords?: number,
  skipGraph?: boolean
): Promise<{ requestId: string; requestIds: string[] }> {
  const payload = {
    sessionId,
    phase,
    phaseGuideline,
    responseMode: buildResponseModeString(responseModeKey),
    userInput,
    projectId,
    ...(targetAvatarRoles && targetAvatarRoles.length > 0 ? { targetAvatarRoles } : {}),
    ...(typeof maxWords === 'number' ? { maxWords } : {}),
    ...(skipGraph ? { skipGraph: true } : {})
  };

  const res = await api.post('/api/sessions/workshop/suggest', payload);

  return { requestId: res.data.requestId, requestIds: res.data.requestIds || [res.data.requestId] };
}

export async function generateTTSViaN8n(
  text: string,
  avatarRole: string
): Promise<{ success: boolean; audioBase64: string; audioUrl: string; format: string; durationMs: number }> {
  const res = await api.post('/api/sessions/workshop/generate-tts', { text, avatarRole });
  let raw = res.data;


  // n8n may wrap in an array: [{data: "..."}] → unwrap
  if (Array.isArray(raw) && raw.length > 0) {
    raw = raw[0];
  }

  // Map n8n response to the same shape as generateTTS
  // n8n returns { data: "<base64>" } for the TTS audio
  const audioBase64 = raw?.data || raw?.audioBase64 || raw?.audio || raw?.base64 || '';
  return {
    success: !!audioBase64,
    audioBase64: audioBase64.startsWith('data:') ? audioBase64 : `data:audio/mp3;base64,${audioBase64}`,
    audioUrl: raw?.audioUrl || '',
    format: raw?.format || 'mp3',
    durationMs: raw?.durationMs || 0
  };
}

export async function extractRequirementsViaN8n(
  sessionId: string,
  transcript: any[],
  customerName?: string,
  personas?: any[],
  guidance?: any[]
): Promise<RequirementsExtractionResponse> {
  const res = await api.post(`/api/sessions/${sessionId}/extract-requirements`, {
    transcript,
    customerName,
    personas,
    guidance
  });

  if (!res.data) {
    throw new Error('n8n returned empty response for requirements extraction');
  }

  // Backend proxy returns same shape as regular extract-requirements route
  return res.data;
}

export async function startFacilitatorGuidanceTask(
  sessionId: string,
  phase: string,
  phaseObjective: string,
  phaseGoal?: string,
  successCriteria?: string[]
): Promise<{ taskId: string }> {
  const res = await api.post('/api/sessions/workshop/guidance', {
    sessionId,
    phase,
    phaseObjective,
    phaseGoal,
    successCriteria
  });

  if (!res.data?.taskId) {
    throw new Error('Failed to start guidance task');
  }

  return { taskId: res.data.taskId };
}

export async function getStoredFacilitatorGuidance(sessionId: string): Promise<FacilitatorGuidance | null> {
  const res = await api.get(`/api/sessions/${sessionId}/guidance/latest`);
  return res.data?.guidance || null;
}

export async function sendChatMessageViaN8n(
  sessionId: string,
  message: string
): Promise<ChatMessage> {

  const res = await api.post(`/api/sessions/${sessionId}/facilitator-chat/message`, { message });

  if (!res.data) {
    throw new Error('n8n returned empty response for chat message');
  }

  const data = res.data;
  return {
    id: data.messageId,
    role: data.role,
    content: data.content,
    timestamp: data.timestamp,
    references: data.references
  };
}

export async function executeQuickActionViaN8n(
  sessionId: string,
  actionId: string
): Promise<ChatMessage> {

  const res = await api.post(`/api/sessions/${sessionId}/facilitator-chat/message`, { actionId });

  if (!res.data) {
    throw new Error('n8n returned empty response for quick action');
  }

  const data = res.data;
  return {
    id: data.messageId,
    role: data.role,
    content: data.content,
    timestamp: data.timestamp,
    references: data.references
  };
}

// ============================================================================
// DEVICE MANAGEMENT VIA N8N
// ============================================================================

export interface DeviceInfo {
  deviceId: string;
  /** `iot` for a device that reports over AWS IoT Core; `backend` for a peripheral (glasses, Stream Deck) the platform registers for a facilitator; absent for a device on n8n. */
  transport?: 'iot' | 'backend';
  /** The facilitator a `backend` peripheral belongs to. */
  ownerEmail?: string;
  displayName?: string;
  deviceType: string;
  locationId: string;
  capabilities: string[];
  ipAddress: string;
  batteryLevel: number;
  batteryVoltage: number;
  isCharging: boolean;
  state: string;
  lastHeartbeat: string;
  sessionId: string | null;
  assignedRole: string | null;
  leaseExpiresAt?: string | null;
  leaseDurationMinutes?: number | null;
  leasedAt?: string | null;
  systemMetrics?: {
    cpuPercent: number;
    memPercent: number;
    diskPercent: number;
    tempC: number;
    codeVersion: string;
    uptimeSeconds: number;
  } | null;
}

// Slot-indexed device roles
type NullableString = string | null | undefined;

export const MAX_CAMERA_SLOTS = 3;
export const MAX_MICROPHONE_SLOTS = 2;
export const MAX_GLASSES_SLOTS = 2;

export const CAMERA_SLOTS = Array.from({ length: MAX_CAMERA_SLOTS }, (_, i) => `camera-${i + 1}`);
export const MICROPHONE_SLOTS = Array.from({ length: MAX_MICROPHONE_SLOTS }, (_, i) => `microphone-${i + 1}`);
export const GLASSES_SLOTS = Array.from({ length: MAX_GLASSES_SLOTS }, (_, i) => `facilitator-hud-${i + 1}`);

export function isCameraRole(role: NullableString): boolean {
  return !!role && /^camera-\d+$/.test(role);
}

export function isGlassesRole(role: NullableString): boolean {
  return !!role && /^facilitator-hud-\d+$/.test(role);
}

export function isGlassesDevice(device: Pick<DeviceInfo, 'assignedRole' | 'deviceType'>): boolean {
  return isGlassesRole(device.assignedRole) || device.deviceType === 'ar-glasses';
}

export function isMicrophoneRole(role: NullableString): boolean {
  return !!role && /^microphone-\d+$/.test(role);
}

export function getSlotNumber(role: string): number {
  const match = role.match(/-(\d+)$/);
  return match ? parseInt(match[1], 10) : 0;
}

export function formatSlotRole(role: string): string {
  if (isCameraRole(role)) return `Camera ${getSlotNumber(role)}`;
  if (isMicrophoneRole(role)) return `Microphone ${getSlotNumber(role)}`;
  if (isGlassesRole(role)) return `AR Glasses ${getSlotNumber(role)}`;
  return role;
}

export interface GlassesSystemMetrics {
  serial?: string;
  firmware?: string;
  arms?: { left?: boolean; right?: boolean };
  role?: 'primary' | 'viewer';
  name?: string;
  ownerEmail?: string;
}

/** systemMetrics may arrive as an object or a JSON string depending on the n8n path. */
export function parseSystemMetrics(device: Pick<DeviceInfo, 'systemMetrics'>): Record<string, unknown> | null {
  const raw = device.systemMetrics as unknown;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? raw as Record<string, unknown> : null;
}

export function getGlassesMetrics(device: Pick<DeviceInfo, 'systemMetrics'>): GlassesSystemMetrics {
  return (parseSystemMetrics(device) ?? {}) as GlassesSystemMetrics;
}

export function getGlassesArmsLabel(arms: GlassesSystemMetrics['arms']): { ok: boolean; label: string } {
  const left = !!arms?.left;
  const right = !!arms?.right;
  if (left && right) return { ok: true, label: 'Both arms connected' };
  if (!left && !right) return { ok: false, label: 'Both arms disconnected' };
  return { ok: false, label: `${left ? 'Right' : 'Left'} arm disconnected` };
}

export async function listDevicesViaN8n(): Promise<{ devices: DeviceInfo[] }> {
  const res = await api.get('/api/devices-n8n');
  return res.data;
}

export async function listAvailableDevicesViaN8n(
  locationId?: string,
  capability?: string
): Promise<{ devices: DeviceInfo[] }> {
  const params = new URLSearchParams();
  if (locationId) params.set('locationId', locationId);
  if (capability) params.set('capability', capability);
  const query = params.toString();
  const res = await api.get(`/api/devices-n8n/available${query ? '?' + query : ''}`);
  return res.data;
}

export async function listSessionDevicesViaN8n(
  sessionId: string
): Promise<{ devices: DeviceInfo[] }> {
  const res = await api.get(`/api/devices-n8n/session/${sessionId}`);
  return res.data;
}

export async function assignDeviceViaN8n(
  deviceId: string,
  role: string,
  sessionId: string,
  leaseDurationMinutes?: number,
  avatarId?: string
): Promise<void> {
  await api.post('/api/devices-n8n/assign', { deviceId, sessionId, role, leaseDurationMinutes, ...(avatarId && { avatarId }) });
}

export async function unassignDeviceViaN8n(deviceId: string): Promise<void> {
  await api.post('/api/devices-n8n/unassign', { deviceId });
}

export async function renewDeviceLeaseViaN8n(
  deviceId: string,
  sessionId: string,
  leaseDurationMinutes?: number
): Promise<void> {
  await api.post('/api/devices-n8n/renew-lease', { deviceId, sessionId, leaseDurationMinutes });
}

export async function renameDevice(
  deviceId: string,
  displayName: string
): Promise<void> {
  await api.put(`/api/devices/${deviceId}/display-name`, { displayName });
}

export async function releaseSpeechViaN8n(
  sessionId: string,
  avatarRole: string,
  commandRowId?: string,
  contributionId?: string
): Promise<{ success: boolean; released: number }> {
  const res = await api.post('/api/sessions/workshop/release-speech', {
    sessionId,
    avatarRole,
    ...(commandRowId && { commandRowId }),
    ...(contributionId && { contributionId })
  });
  return res.data;
}

/** Reports a contribution played in the browser; fire-and-forget, never blocks playback. */
export function reportContributionPlayed(sessionId: string, contributionId: string, avatarRole?: string): void {
  api
    .post(`/api/sessions/${encodeURIComponent(sessionId)}/contributions/${encodeURIComponent(contributionId)}/played`, { avatarRole })
    .catch(() => undefined);
}

export async function approveAvatarContributionViaN8n(
  sessionId: string,
  avatarRole: string,
  text: string,
  audioBase64: string,
  stageOnly?: boolean,
  contributionId?: string
): Promise<{ success: boolean; playbackMode: string; queued?: boolean; commandRowId?: string }> {
  const res = await api.post('/api/sessions/workshop/approve-avatar', {
    sessionId,
    avatarRole,
    text,
    audioBase64,
    ...(stageOnly && { stageOnly: true }),
    ...(contributionId && { contributionId })
  });
  return res.data;
}

// ============================================================================
// ASYNC TTS STAGING (fire-and-forget with callback)
// ============================================================================

export interface StagedAudioEntry {
  requestId: string;
  status: 'generating' | 'ready' | 'failed';
  text: string;
  avatarRole: string;
  startedAt: string;
  readyAt?: string;
  audioBase64?: string;
  audioUrl?: string;
  s3Key?: string;
  format?: string;
  durationMs?: number;
  queued?: boolean;
  error?: string;
  commandRowId?: string;
}

export async function stageAudioAsync(
  sessionId: string,
  avatarRole: string,
  text: string
): Promise<{ requestId: string; status: string }> {
  const res = await api.post('/api/sessions/workshop/stage-audio', {
    sessionId,
    avatarRole,
    text
  });
  return res.data;
}

export async function getStagedAudio(
  sessionId: string
): Promise<Record<string, StagedAudioEntry>> {
  const res = await api.get(`/api/sessions/${sessionId}/staged-audio`);
  return res.data.stagedAudio || {};
}

export async function clearStagedAudio(
  sessionId: string,
  avatarRole: string
): Promise<void> {
  await api.delete(`/api/sessions/${sessionId}/staged-audio/${avatarRole}`);
}

export async function stopSpeechViaN8n(sessionId: string): Promise<{ success: boolean }> {
  const res = await api.post('/api/sessions/workshop/stop-speech', { sessionId });
  return res.data;
}

export async function muteMicViaN8n(sessionId: string): Promise<{ success: boolean }> {
  const res = await api.post('/api/sessions/workshop/mute-mic', { sessionId });
  return res.data;
}

export async function unmuteMicViaN8n(sessionId: string): Promise<{ success: boolean }> {
  const res = await api.post('/api/sessions/workshop/unmute-mic', { sessionId });
  return res.data;
}

export async function syncCredoViaN8n(
  sessionId: string,
  sessionName: string,
  transcript: any[]
): Promise<{
  success: boolean;
  useCaseId: string;
  riskClassification: { level: number; label: string };
  surveys: Array<{
    surveyId: string;
    surveyName: string;
    questions: Array<{ questionId: string; question: string; answer: string }>;
  }>;
}> {

  const res = await api.post(`/api/sessions/workshop/${sessionId}/credo/sync`, {
    sessionName,
    transcript
  });

  if (!res.data) {
    throw new Error('n8n returned empty response for credo sync');
  }

  return res.data;
}

export interface StoryAction {
  type: 'approve' | 'reject' | 'set_priority' | 'add_ac' | 'reword';
  storyNumber: number;
  storyId: string;
  priority?: 'high' | 'medium' | 'low';
  criteria?: string[];
  suggestion?: string;
}

export interface DeepDiveMessage {
  id: string;
  agentType: 'specialist' | 'persona';
  personaId: string;
  personaName: string;
  message: string;
  timestamp: string;
  actions?: StoryAction[];
}

export interface NFRAction {
  type: 'approve' | 'reject' | 'set_priority' | 'set_category' | 'add_ac' | 'reword';
  nfrId: string;
  priority?: 'high' | 'medium' | 'low';
  category?: 'performance' | 'security' | 'scalability' | 'availability' | 'usability' | 'maintainability' | 'compliance';
  criteria?: string[];
  field?: 'title' | 'description';
  suggestion?: string;
}

export interface NFRDeepDiveMessage {
  id: string;
  agentType: 'specialist' | 'persona';
  personaId: string;
  personaName: string;
  message: string;
  timestamp: string;
  actions?: NFRAction[];
}

export interface ADRAction {
  type: 'approve' | 'reject' | 'set_status' | 'add_alternative' | 'reword';
  adrId: string;
  status?: 'proposed' | 'accepted' | 'deprecated' | 'superseded';
  alternatives?: string[];
  field?: 'title' | 'context' | 'decision' | 'consequences';
  suggestion?: string;
}

export interface ADRDeepDiveMessage {
  id: string;
  agentType: 'specialist' | 'persona';
  personaId: string;
  personaName: string;
  message: string;
  timestamp: string;
  actions?: ADRAction[];
}

export async function triggerStoryDeepDiveAnalysis(
  sessionId: string,
  personaId: string,
  personaName: string,
  personaRole: string,
  personaDescription: string,
  stories: Array<{ id: string; number: number; story: string; acceptanceCriteria: string[]; priority: string }>
): Promise<{ requestId: string }> {
  const res = await api.post(`/api/sessions/${sessionId}/story-deep-dive/analyze`, {
    personaId,
    personaName,
    personaRole,
    personaDescription,
    stories,
  });
  return res.data;
}

export async function updateStoryContent(
  sessionId: string,
  storyId: string,
  updates: {
    story?: string;
    acceptanceCriteria?: string[];
    priority?: 'high' | 'medium' | 'low';
    notes?: string[];
    groupId?: string;
    groupLabel?: string;
    persona?: string;
  }
): Promise<void> {
  await api.patch(`/api/sessions/${sessionId}/user-stories/${storyId}`, updates);
}

export async function getDeepDiveMessages(sessionId: string): Promise<DeepDiveMessage[]> {
  const res = await api.get(`/api/sessions/${sessionId}/story-deep-dive/messages`);
  return res.data.messages || [];
}

export async function clearDeepDiveMessages(sessionId: string): Promise<void> {
  await api.delete(`/api/sessions/${sessionId}/story-deep-dive/messages`);
}

export async function updateStoryApproval(
  sessionId: string,
  storyId: string,
  approvalStatus: 'pending' | 'approved' | 'rejected',
  rank?: number
): Promise<void> {
  await api.patch(`/api/sessions/${sessionId}/user-stories/${storyId}`, {
    approvalStatus,
    ...(rank !== undefined ? { rank } : {}),
  });
}

export async function lockStory(sessionId: string, storyId: string, reason?: string): Promise<void> {
  await api.patch(`/api/sessions/${sessionId}/user-stories/${storyId}/lock`, {
    ...(reason !== undefined ? { reason } : {}),
  });
}

export async function unlockStory(sessionId: string, storyId: string): Promise<void> {
  await api.patch(`/api/sessions/${sessionId}/user-stories/${storyId}/unlock`, {});
}

export async function exportPRD(sessionId: string): Promise<Blob> {
  const res = await api.get(`/api/sessions/${sessionId}/story-deep-dive/export/prd`, {
    responseType: 'blob',
  });
  return res.data;
}
